/********************************************************************************
 * Copyright (c) 2020 Cedalo GmbH
 *
 * This program and the accompanying materials are made available under the
 * terms of the Eclipse Public License 2.0 which is available at
 * http://www.eclipse.org/legal/epl-2.0.
 *
 * SPDX-License-Identifier: EPL-2.0
 *
 ********************************************************************************/
import { MenuItem } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import TextField from '@mui/material/TextField';
import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Path } from '../../helper/Path';
import DialogButton from '../../ui/utils/DialogButton';
import { withUser } from '../../UserProvider';
import { createApp } from './AppGraphQL';

const ERROR_MESSAGES = {
	DUPLICATE: (
		<FormattedMessage id="Error.DuplicateName" defaultMessage="Name already taken, please select unique name" />
	),
	EMPTY: <FormattedMessage id="Error.EmptyName" defaultMessage="Name cannot be empty" />,
	INVALID: <FormattedMessage id="Error.InvalidName" defaultMessage="Invalid Name" />
};

export const NewAppDialog = (props) => {
	const [error, setError] = useState('');
	const [name, setName] = useState('');
	const [loading, setLoading] = useState(false);
	const [scopeId, setScopeId] = useState(props.scopes[0].id);
	const [templates, setTemplates] = useState([]);
	const [template, setTemplate] = useState('none');

	useEffect(() => {
		if (props.workSpace.includes('allWS')) {
			setScopeId(props.scopes[0].id);
		} else {
			setScopeId(props.workSpace);
		}
	}, [props.workSpace]);

	const reset = () => {
		setError('');
		setName('');
	};

	const handleClose = () => {
		reset();
		props.onClose();
	};

	useEffect(() => {
		const templatesUrl = 'https://dev.cedalo.cloud/http/HygXWoX3Mj/templates';

		fetch(templatesUrl)
			.then((resp) => resp.json())
			.then((data) => {
				const tmpls = [];
				if (!data.data) {
					return;
				}
				data.data.forEach((tmpl, index) => {
					tmpls[index] = {
						id: tmpl.id,
						name: tmpl.name,
						description: tmpl.description || 'Missing Description',
						category: tmpl.category
					};
				});
				setTemplates(tmpls);
			})
			.catch(() => {
				// console.log(error);
			});
	}, []);


	const isNameValid = (newName) => newName.length > 0;
	const isNameUsed = (newName, scope) => props.apps.some((app) => app.name === newName && app.scope.id === scope);

	const validateName = (newName, scope = scopeId) => {
		setName(newName);
		if (!newName || newName.length < 1) {
			setError(ERROR_MESSAGES.EMPTY);
			return false;
		}
		if (isNameUsed(newName, scope)) {
			setError(ERROR_MESSAGES.DUPLICATE);
			return false;
		}
		if (!isNameValid(newName)) {
			setError(ERROR_MESSAGES.INVALID);
			return false;
		}
		setError('');
		return true;
	};

	const handleNameChange = (event) => {
		event.preventDefault();
		validateName(event.target.value);
	};

	const handleTemplateChange = (event) => {
		setTemplate(event.target.value);
	};

	const handleScopeChange = (event) => {
		const newScope = event.target.value;
		event.preventDefault();
		setScopeId(newScope);
		validateName(name, newScope);
	};

	const handleSubmit = async () => {
		if (validateName(name)) {
			if (!error) {
				setLoading(true);
				const newAppId = await createApp({ id: scopeId }, name);
				props.openPage(Path.app(newAppId));
				setLoading(false);
				handleClose();
			}
		}
	};

	if (!props.open) {
		return null;
	}

	return (
		<Dialog open={props.open} onClose={handleClose} maxWidth={false}>
			<DialogTitle>
				<FormattedMessage id="NewAppDialog.Title" defaultMessage="New" />
			</DialogTitle>
			<DialogContent
				style={{
					minWidth: '600px'
				}}
			>
				<TextField
					variant="outlined"
					size="small"
					label={<FormattedMessage id="NewAppDialog.NameField" defaultMessage="Name" />}
					id="name"
					name="name"
					fullWidth
					margin="normal"
					value={name}
					onChange={handleNameChange}
					error={!!error}
					helperText={error}
				/>
				{templates.length ? (
					<TextField
						variant="outlined"
						size="small"
						select
						label={<FormattedMessage id="NewAppDialog.UseTemplate" defaultMessage="Use Template" />}
						fullWidth
						margin="normal"
						value={template}
						onChange={handleTemplateChange}
					>
						<MenuItem value="none" key="none">
							<FormattedMessage id="NewAppDialog.NoTemplate" defaultMessage="No Template" />
						</MenuItem>
						{templates.map((tmpl) => (
							<MenuItem value={tmpl.id} key={tmpl.id}>
								{tmpl.name}
							</MenuItem>
						))}
					</TextField>
				) : null}
				{props.scopes.length > 1 ? (
					<TextField
						variant="outlined"
						size="small"
						select
						label={<FormattedMessage id="NewAppDialog.Workspace" defaultMessage="Workspace" />}
						id="scope"
						name="scope"
						fullWidth
						margin="normal"
						value={scopeId}
						onChange={handleScopeChange}
					>
						{props.scopes.map((scope) => (
							<MenuItem value={scope.id} key={scope.id}>
								{scope.name}
							</MenuItem>
						))}
					</TextField>
				) : null}
			</DialogContent>
			<Divider />
			<DialogActions>
				<DialogButton margin onClick={handleClose} disabled={loading}>
					<FormattedMessage id="Dialog.Cancel" defaultMessage="Cancel" />
				</DialogButton>
				<DialogButton margin onClick={handleSubmit} defaultButton disabled={!!error || loading}>
					{loading && (
						<CircularProgress
							size={24}
							sx={{
								position: 'absolute',
								top: '50%',
								left: '50%',
								marginTop: '-12px',
								marginLeft: '-12px',
							}}
						/>
					)}
					<FormattedMessage id="NewAppDialog.Add" defaultMessage="Add" />
				</DialogButton>
			</DialogActions>
		</Dialog>
	);
};

export default withUser(({ user }) => ({
	scopes: user.scopes.filter(({ rights }) => rights.includes('machine.edit'))
}))(NewAppDialog);
